import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  ActionIcon,
  Button,
  Container,
  Flex,
  Group,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { useForm, zodResolver } from '@mantine/form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { addBatchLesson } from '@/entities/books/api/book-lessons-api';
import { AudioProcessingStatus } from '@/entities/books/model/types';
import { useDirtyForm } from '@/shared/hooks/use-dirty-form';
import {
  useCurrentProject,
  useProjectPath,
} from '@/shared/projects/project-path';
import { useAudioUploadStore } from '@/shared/stores/audio-upload.store';

const lessonBatchFormSchema = z.object({
  timecodes: z
    .string()
    .min(1, 'Таймкоды обязательны')
    .refine(
      (val) => {
        // Проверяем формат: HH:MM:SS Заголовок
        const lines = val.trim().split('\n');
        const timecodePattern = /^\d{2}:\d{2}:\d{2}\s+.+$/;

        return (
          lines.every((line) => {
            const trimmedLine = line.trim();
            return trimmedLine === '' || timecodePattern.test(trimmedLine);
          }) && lines.some((line) => line.trim() !== '')
        );
      },
      {
        message:
          'Неверный формат таймкодов. Используйте формат "HH:MM:SS Заголовок", один на строку',
      },
    ),
});

const initialLessonBatchInfo = {
  timecodes: '',
};

export const BookLessonsBatchDetailPage = () => {
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [audioPreviewUrl, setAudioPreviewUrl] = useState<string | null>(null);
  const { bookId } = useParams();
  const navigate = useNavigate();
  const withProject = useProjectPath();
  const project = useCurrentProject();

  const isAudioUploading = useAudioUploadStore((state) => state.isUploading);
  const setIsAudioUploading = useAudioUploadStore(
    (state) => state.setIsUploading,
  );
  const setStatusText = useAudioUploadStore((state) => state.setStatusText);

  const form = useForm({
    initialValues: initialLessonBatchInfo,
    validate: zodResolver(lessonBatchFormSchema),
    validateInputOnChange: true,
    validateInputOnBlur: true,
  });

  useDirtyForm(form.isDirty() || Boolean(audioFile));

  const isFormReady =
    audioFile && form.values.timecodes.trim() && !form.errors.timecodes;

  const queryClient = useQueryClient();

  const uploadMutation = useMutation({
    mutationFn: (formData: FormData) => addBatchLesson(formData),
    onMutate: () => {
      setIsAudioUploading(true);
      setStatusText(AudioProcessingStatus.WAITING);
      navigate(withProject(`/books/${bookId}/description`));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [project, 'audioUploadStatus'],
      });
      setStatusText(AudioProcessingStatus.WAITING);
    },
    onError: (error) => {
      console.error('Ошибка при загрузке файлов:', error);
      setIsAudioUploading(false);
      setStatusText(AudioProcessingStatus.ERROR);
    },
  });

  useEffect(() => {
    return () => {
      if (audioPreviewUrl) {
        URL.revokeObjectURL(audioPreviewUrl);
      }
    };
  }, [audioPreviewUrl]);

  const handleAudioDrop = (files: File[]) => {
    const file = files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setAudioPreviewUrl(previewUrl);
      setAudioFile(file);
    }
  };

  const handleSubmit = async (values: typeof form.values) => {
    if (!bookId || !audioFile) {
      console.error('Отсутствуют требуемые данные: bookId или audioFile');
      return;
    }

    setIsAudioUploading(true);
    const formData = new FormData();
    formData.append('audio', audioFile);
    formData.append('bookId', bookId);
    formData.append('timecodes', values.timecodes);

    await uploadMutation.mutate(formData);
  };

  const handleClearAudio = () => {
    if (audioPreviewUrl) {
      URL.revokeObjectURL(audioPreviewUrl);
    }
    setAudioFile(null);
    setAudioPreviewUrl(null);
  };

  const handleCancel = () => {
    navigate(withProject(`/books/${bookId}/description`));
  };

  return (
    <Container size={1400} mt={50}>
      <Title order={1} mb={24}>
        Создание уроков
      </Title>

      <Stack>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Textarea
            mb={20}
            label="Таймкоды"
            key={form.key('timecodes')}
            name="timecodes"
            placeholder={`Введите таймкоды в формате:
              00:00:00 Предисловие
              00:01:48 Правило первое
              00:02:27 Правило второе`}
            {...form.getInputProps('timecodes')}
            autosize
            minRows={2}
            resize="none"
          />
          <Title order={5}>Аудиофайл</Title>
          <Flex direction="column" gap={16} mt="sm">
            <Dropzone
              onDrop={handleAudioDrop}
              accept={['audio/mpeg']}
              multiple={false}
              h={150}
              w={250}
            >
              <Flex direction="column" align="center" gap={12}>
                <Text>Перетащите файл или выберите</Text>
                <Button>Загрузить аудио</Button>
              </Flex>
            </Dropzone>

            {audioFile && (
              <Flex direction="row" align="center" gap={8} maw="250px" w="100%">
                {audioPreviewUrl && (
                  // eslint-disable-next-line jsx-a11y/media-has-caption
                  <audio controls src={audioPreviewUrl}>
                    Ваш браузер не поддерживает аудио элемент.
                  </audio>
                )}
                <ActionIcon variant="outline" onClick={handleClearAudio}>
                  <XMarkIcon width={20} height={20} />
                </ActionIcon>
              </Flex>
            )}
          </Flex>

          <Group my={32}>
            <Button type="submit" disabled={!isFormReady || isAudioUploading}>
              {isAudioUploading ? 'Загрузка...' : 'Сохранить'}
            </Button>
            <Button onClick={handleCancel} variant="outline" color="grey">
              Отменить
            </Button>
          </Group>
        </form>
      </Stack>
    </Container>
  );
};
